import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { errorCallback, makeUrl, succesCallbackEmpty } from '../../const/const';
import { MAKE_DELIVERY_REQUEST, MAKE_USER_REQUEST, getFetchRequest, postFetchRequest } from '../../store/requests';

const sortByDefaultFirst = (list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
        if (a.isDefault === b.isDefault) return 0;
        return a.isDefault ? -1 : 1;
    });
};

const TabDelivery = ({ userId }) => {
    const [addFormIsOpen, setAddFormIsOpen] = useState(false);
    // Контактная информация
    const [recipientName, setRecipientName] = useState('');
    const [recipientPhone, setRecipientPhone] = useState('');
    // Адрес — разметка как на макете
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [streetName, setStreetName] = useState('');
    const [house, setHouse] = useState('');
    const [corpus, setCorpus] = useState('');
    const [building, setBuilding] = useState('');
    const [other, setOther] = useState('');
    const [addressExtra, setAddressExtra] = useState('');
    // Время работы
    const [workFrom, setWorkFrom] = useState('');
    const [workTo, setWorkTo] = useState('');
    const [lunchFrom, setLunchFrom] = useState('');
    const [lunchTo, setLunchTo] = useState('');
    const [error, setError] = useState({ type: '', message: '' });
    const [deliveriesList, setDeliveriesList] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const canSave = Boolean(recipientName && recipientPhone && zipCode && city && streetName && house);

    // Маска времени "99:99" с ограничением 00-23 и 00-59.
    // Поведение ввода: 2 -> "2"; 21 -> "21:"; 21 3 -> "21:3"; 21 34 -> "21:34".
    const maskTime = (value) => {
        const digits = (value || '').replace(/\D/g, '').slice(0, 4);
        if (digits.length === 0) return '';

        // Если введена только одна цифра часов — показываем её как есть
        if (digits.length === 1) {
            return digits;
        }

        // Две и более цифры — нормализуем часы в диапазон 00..23
        let hhNum = parseInt(digits.slice(0, 2), 10);
        if (Number.isNaN(hhNum)) hhNum = 0;
        if (hhNum > 23) hhNum = 23;
        const hh = String(hhNum).padStart(2, '0');

        if (digits.length === 2) {
            return `${hh}:`;
        }

        // Минуты
        const mmRaw = digits.slice(2, 4);
        if (mmRaw.length === 1) {
            return `${hh}:${mmRaw}`;
        }
        let mmNum = parseInt(mmRaw, 10);
        if (Number.isNaN(mmNum)) mmNum = 0;
        if (mmNum > 59) mmNum = 59;
        const mm = String(mmNum).padStart(2, '0');
        return `${hh}:${mm}`;
    };

    // refs для полей времени, чтобы управлять курсором
    const workFromRef = useRef(null);
    const workToRef = useRef(null);
    const lunchFromRef = useRef(null);
    const lunchToRef = useRef(null);

    // Универсальный обработчик изменения времени с сохранением позиции курсора
    const handleTimeChange = (setter, inputRef) => (e) => {
        const input = e.target;
        const raw = input.value;
        const caret = input.selectionStart || 0;
        const digitsBefore = raw.slice(0, caret).replace(/\D/g, '').length;
        const masked = maskTime(raw);
        setter(masked);
        // Восстановим позицию курсора по числу цифр слева
        requestAnimationFrame(() => {
            const m = masked || '';
            let pos = 0;
            let count = 0;
            while (pos < m.length && count < digitsBefore) {
                if (/\d/.test(m[pos])) count += 1;
                pos += 1;
            }
            const el = inputRef.current;
            if (el) el.setSelectionRange(pos, pos);
        });
    };

    const successListCallback = (response) => {
        if (Array.isArray(response) && response.length) {
            setDeliveriesList(sortByDefaultFirst(response));
        } else {
            setAddFormIsOpen(true);
        }
    };

    const saveNewCallback = (response) => {
        if (!response || (typeof response.ok === 'boolean' && !response.ok) || (typeof response.ok !== 'boolean' && !response.id)) {
            setError({ type: 'save', message: 'Не удалось сохранить адрес доставки' });
            return;
        }
        setAddFormIsOpen(false);
        const deliveryAddress = composeAddress();
        const workTime = composeTime(workFrom, workTo);
        const lunchTime = composeTime(lunchFrom, lunchTo);
        if (deliveriesList.length === 0) {
            setDeliveriesList(prev => [...prev, { id: response.id, recipientName, recipientPhone, deliveryAddress, workTime, lunchTime, other, extraInfo: addressExtra, isDefault: true }]);
            postFetchRequest(makeUrl([MAKE_DELIVERY_REQUEST, response.id, '/default/']), '', succesCallbackEmpty, errorCallback, true);
        } else if (response && response.id) {
            setDeliveriesList(prev => [...prev, { ...response, recipientName, recipientPhone, deliveryAddress, workTime, lunchTime, other, extraInfo: addressExtra }]);
        }
    };

    const updateCallback = (response) => {
        if (response && typeof response.ok === 'boolean') {
            if (!response.ok) {
                setError({ type: 'save', message: 'Не удалось сохранить адрес доставки' });
                return;
            }
        } else if (!response || (response && !response.id)) {
            setError({ type: 'save', message: 'Не удалось сохранить адрес доставки' });
            return;
        }

        setAddFormIsOpen(false);
        setIsEditing(false);
        setEditingId(null);
        setError({ type: '', message: '' });
        const deliveryAddress = composeAddress();
        const workTime = composeTime(workFrom, workTo);
        const lunchTime = composeTime(lunchFrom, lunchTo);
        setDeliveriesList(prev => prev.map(it => {
            if (it.id === (response?.id || editingId)) {
                return {
                    ...it,
                    recipientName,
                    recipientPhone,
                    deliveryAddress,
                    workTime,
                    lunchTime,
                    other,
                    extraInfo: addressExtra,
                };
            }
            return it;
        }));
    };

    const composeAddress = () => {
        const parts = [];
        if (zipCode) parts.push(zipCode);
        if (city) parts.push(city);
        if (streetName) parts.push(streetName);
        if (house) parts.push(`д. ${house}`);
        if (corpus) parts.push(`корп. ${corpus}`);
        if (building) parts.push(`стр. ${building}`);
        if (other) parts.push(other);
        if (addressExtra) parts.push(addressExtra);
        return parts.join(', ');
    };

    const composeTime = (from, to) => {
        if (!from && !to) return '';
        if (from && to) return `${from} - ${to}`;
        return from || to || '';
    };

    const handleSave = () => {
        if (!canSave) {
            setError({ type: 'validate', message: 'Заполните получателя, телефон и адрес доставки' });
            return;
        }
        const payload = {
            recipientName,
            recipientPhone,
            deliveryAddress: composeAddress(),
            workTime: composeTime(workFrom, workTo),
            lunchTime: composeTime(lunchFrom, lunchTo),
            other,
            extraInfo: addressExtra,
        };
        if (isEditing) {
            const data = payload;
            postFetchRequest(makeUrl([MAKE_DELIVERY_REQUEST, editingId, '/']), data, updateCallback, errorCallback, true);
        } else {
            const data = payload;
            postFetchRequest(makeUrl([MAKE_USER_REQUEST, userId, '/delivery/']), data, saveNewCallback, errorCallback);
        }
    };

    useEffect(() => {
        if (userId) {
            getFetchRequest(makeUrl([MAKE_USER_REQUEST, userId, '/delivery/']), successListCallback, errorCallback);
        }
    }, [userId]);

    const handleSetDefault = (item) => {
        const list = deliveriesList.map(it => ({ ...it, isDefault: it.id === item.id }));
        setDeliveriesList(list);
        postFetchRequest(makeUrl([MAKE_DELIVERY_REQUEST, item.id, '/default/']), '', succesCallbackEmpty, errorCallback, true);
    };

    const handleOpenNewForm = () => {
        setRecipientName('');
        setRecipientPhone('');
        setZipCode('');
        setCity('');
        setStreetName('');
        setHouse('');
        setCorpus('');
        setBuilding('');
        setOther('');
        setAddressExtra('');
        setWorkFrom('');
        setWorkTo('');
        setLunchFrom('');
        setLunchTo('');
        setError({ type: '', message: '' });
        setIsEditing(false);
        setEditingId(null);
        setAddFormIsOpen(!addFormIsOpen);
    };

    const handleOpenEditForm = (e, item) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditingId(item.id);
        setRecipientName(item.recipientName || '');
        setRecipientPhone(item.recipientPhone || '');
        // Пытаемся разложить адрес по полям (грубая эвристика)
        const address = item.deliveryAddress || '';
        const parts = address.split(',').map(s => s.trim());
        setZipCode(parts[0] || '');
        setCity(parts[1] || '');
        setStreetName(parts[2] || '');
        const houseMatch = address.match(/д\.?\s*([\w-]+)/i);
        setHouse(houseMatch ? houseMatch[1] : '');
        const corpMatch = address.match(/корп\.?\s*([\w-]+)/i);
        setCorpus(corpMatch ? corpMatch[1] : '');
        const buildMatch = address.match(/стр\.?\s*([\w-]+)/i);
        setBuilding(buildMatch ? buildMatch[1] : '');
        setOther(item.other || '');
        setAddressExtra(item.extraInfo || '');
        const workSplit = (item.workTime || '').split('-').map(s => s.trim());
        setWorkFrom(workSplit[0] || '');
        setWorkTo(workSplit[1] || '');
        const lunchSplit = (item.lunchTime || '').split('-').map(s => s.trim());
        setLunchFrom(lunchSplit[0] || '');
        setLunchTo(lunchSplit[1] || '');
        setError({ type: '', message: '' });
        setAddFormIsOpen(true);
    };

    return (
        <>
            <div className="setup-customer__section tab-delivery">
                <h3 className="setup-customer__title">{addFormIsOpen ? (isEditing ? 'Редактировать адрес доставки' : 'Новый адрес доставки') : 'Контактная информация'}</h3>
                {!addFormIsOpen ? (
                    <>
                        {deliveriesList.length ? (
                            <div className="setup-customer__cards">
                                {deliveriesList.map(it => (
                                    <div key={it.id} className={`setup-customer__card${!it.isDefault ? ' non-active' : ''}`} onClick={() => handleSetDefault(it)}>
                                        <div className="setup-customer__card-details">
                                            <div className="setup-customer__detail-row">
                                                <span className="setup-customer__detail-label">Получатель</span>
                                                <span className="setup-customer__detail-value">{it.recipientName}</span>
                                            </div>
                                            <div className="setup-customer__detail-row">
                                                <span className="setup-customer__detail-label">Телефон</span>
                                                <span className="setup-customer__detail-value">{it.recipientPhone}</span>
                                            </div>
                                            <div className="setup-customer__detail-row">
                                                <span className="setup-customer__detail-label">Адрес доставки</span>
                                                <span className="setup-customer__detail-value setup-customer__detail-value--multiline double-line">{it.deliveryAddress}</span>
                                            </div>
                                            <div className="setup-customer__detail-row detail-row">
                                                <span className="setup-customer__detail-label">Время работы</span>
                                                <span className="setup-customer__detail-value">{it.workTime || '−'}</span>
                                            </div>
                                            <div className="setup-customer__detail-row detail-row">
                                                <span className="setup-customer__detail-label">Обед</span>
                                                <span className="setup-customer__detail-value">{it.lunchTime || '−'}</span>
                                            </div>
                                        </div>
                                        <div className="setup-customer__card-footer">
                                            <label className="setup-customer__switch" onClick={(e) => e.stopPropagation()}>
                                                <span className="setup-customer__switch-text">Выбрать</span>
                                                <input
                                                    type="checkbox"
                                                    checked={it.isDefault}
                                                    onChange={() => handleSetDefault(it)}
                                                    className="setup-customer__switch-input"
                                                />
                                                <span className="setup-customer__switch-slider"></span>
                                            </label>
                                            <button type="button" className="setup-customer__edit-action-btn" onClick={(e) => handleOpenEditForm(e, it)}>Редактировать</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="setup-customer__new">ДЛЯ ОФОРМЛЕНИЯ ПОКУПКИ ДОБАВЬТЕ АДРЕС ДОСТАВКИ</p>
                        )}
                        <div className="setup-customer__add-button">
                            <button className="setup-customer__add-btn add-delivery-btn" onClick={handleOpenNewForm}>
                                + Добавить адрес и получателя
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="setup-customer__form-section">
                        {/* Контактная информация */}
                        <form className="profile-form setup-customer__form first-row">
                            <label name="recipient-name" className="profile-form__item">Получатель <span className="required">*</span>
                                <input className="profile-form__input orange-bg" placeholder="−" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
                            </label>
                            <label name="recipient-phone" className="profile-form__item">Телефон <span className="required">*</span>
                                <input className="profile-form__input orange-bg" placeholder="−" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} />
                            </label>
                        </form>

                        {/* Адрес доставки */}
                        <h3 className="setup-customer__title title-hours">Адрес доставки</h3>
                        <form className="profile-form setup-customer__form">
                            <div className="profile-form__two-columns address-row">
                                <div className="profile-form__left-column">
                                    <label name="zip" className="profile-form__item">Индекс <span className="required">*</span>
                                        <input className="profile-form__input orange-bg" placeholder="−" value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
                                    </label>
                                </div>
                                <div className="profile-form__right-column">
                                    <label name="city" className="profile-form__item">Город <span className="required">*</span>
                                        <input className="profile-form__input orange-bg" placeholder="−" value={city} onChange={(e) => setCity(e.target.value)} />
                                    </label>
                                </div>
                            </div>
                            <label name="street" className="profile-form__item street-row">Название улицы/ проспекта <span className="required">*</span>
                                <input className="profile-form__input orange-bg" placeholder="−" value={streetName} onChange={(e) => setStreetName(e.target.value)} />
                            </label>
                            <div className="profile-form__two-columns">
                                <div className="profile-form__left-column">
                                    <label name="house" className="profile-form__item">Дом <span className="required">*</span>
                                        <input className="profile-form__input orange-bg" placeholder="−" value={house} onChange={(e) => setHouse(e.target.value)} />
                                    </label>
                                    <label name="building" className="profile-form__item">Строение
                                        <input className="profile-form__input orange-bg" placeholder="−" value={building} onChange={(e) => setBuilding(e.target.value)} />
                                    </label>
                                </div>
                                <div className="profile-form__right-column">
                                    <label name="corpus" className="profile-form__item">Корпус
                                        <input className="profile-form__input orange-bg" placeholder="−" value={corpus} onChange={(e) => setCorpus(e.target.value)} />
                                    </label>
                                    <label name="other" className="profile-form__item">Другое
                                        <input className="profile-form__input orange-bg" placeholder="−" value={other} onChange={(e) => setOther(e.target.value)} />
                                    </label>
                                </div>
                            </div>
                            <label name="extra" className="profile-form__item">Дополнительная информация
                                <textarea className="profile-form__input double-line orange-bg" placeholder="−" value={addressExtra} onChange={(e) => setAddressExtra(e.target.value)}></textarea>
                            </label>
                        </form>

                        {/* Время работы */}
                        <h3 className="setup-customer__title title-hours">Время работы</h3>
                        <form className="profile-form setup-customer__form hours-row">
                            <div className="profile-form__two-columns hours-row">
                                <div className="profile-form__left-column">
                                    <label name="work-from" className="profile-form__item">С
                                        <input ref={workFromRef} className="profile-form__input orange-bg" placeholder="-" value={workFrom} onChange={handleTimeChange(setWorkFrom, workFromRef)} />
                                    </label>
                                </div>
                                <div className="profile-form__right-column">
                                    <label name="work-to" className="profile-form__item">До
                                        <input ref={workToRef} className="profile-form__input orange-bg" placeholder="-" value={workTo} onChange={handleTimeChange(setWorkTo, workToRef)} />
                                    </label>
                                </div>
                            </div>
                            <div className="profile-form__two-columns hours-row">
                                <div className="profile-form__left-column">
                                    <label name="lunch-from" className="profile-form__item">Обед с
                                        <input ref={lunchFromRef} className="profile-form__input orange-bg" placeholder="-" value={lunchFrom} onChange={handleTimeChange(setLunchFrom, lunchFromRef)} />
                                    </label>
                                </div>
                                <div className="profile-form__right-column">
                                    <label name="lunch-to" className="profile-form__item">До
                                        <input ref={lunchToRef} className="profile-form__input orange-bg" placeholder="-" value={lunchTo} onChange={handleTimeChange(setLunchTo, lunchToRef)} />
                                    </label>
                                </div>
                            </div>
                            <div className="profile-form__submit-section setup-org">
                                <button 
                                    className={'close-change-password-btn'} 
                                    type="button" 
                                    onClick={handleOpenNewForm}
                                >
                                    Отменить
                                </button>
                                <button 
                                    onClick={handleSave} 
                                    className={`order-detail-btn order-detail-btn-submit setup-customer__button ${!canSave ? 'button_disabled' : ''}`}
                                    type="button" 
                                    disabled={!canSave}
                                >
                                    Сохранить
                                </button>
                            </div>
                            {error.type && (error.type === 'save' || error.type === 'validate') ? <p className='alert alert__bottom'>{error.message}</p> : ''}
                        </form>
                    </div>
                )}
            </div>
        </>
    );
};

TabDelivery.propTypes = {
    userId: PropTypes.string,
};

export default TabDelivery;


